import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ThemeProvider } from 'styled-components';

import { ShortCodeService } from '@tager/web-core';
import { ModalProvider } from '@tager/web-components';

import { useTypedSelector } from '@/store/store';
import { selectSinglePost } from '@/store/reducers/single-post';
import { media } from '@/utils/mixin';
import { insertTitlesAttributesToHtml } from '@/modules/SinglePost/components/utils';
import { colors } from '@/constants/theme';
import ImagesSlider from '@/components/ImagesSlider';
import { useStyledComponentsTheme } from '@/theme/hooks';

import TextIntro from './components/TextIntro';
import ProsAndCons from './components/ProsAndCons';
import BlockQuote from './components/BlockQuote';
import SportMode from './components/SportMode';
import Youtube from './components/Youtube';

const textIntroShortCodeService = new ShortCodeService('intro', ['text']);
const plusMinusShortCodeService = new ShortCodeService('plus_minus', [
  'plus',
  'minus',
]);
const galleryShortCodeService = new ShortCodeService('gallery', ['number']);
const blockquoteShortCodeService = new ShortCodeService('blockquote', [
  'text',
  'author',
  'position',
]);
const listWithTitleShortCodeService = new ShortCodeService('list_with_title', [
  'title',
  'features',
]);
const videoShortCodeService = new ShortCodeService('youtube', ['id', 'label']);

function Content() {
  const theme = useStyledComponentsTheme();
  const page = useTypedSelector(selectSinglePost);

  const [content, setContent] = useState<string>(page?.body ?? '');

  useEffect(() => {
    if (!page || !page.body) {
      return;
    }

    let replacedText = insertTitlesAttributesToHtml({
      body: page.body,
    });

    if (!replacedText) {
      return;
    }

    replacedText =
      textIntroShortCodeService.replaceShortCodeByElement(replacedText);

    replacedText =
      plusMinusShortCodeService.replaceShortCodeByElement(replacedText);

    replacedText =
      galleryShortCodeService.replaceShortCodeByElement(replacedText);

    replacedText =
      blockquoteShortCodeService.replaceShortCodeByElement(replacedText);

    replacedText =
      listWithTitleShortCodeService.replaceShortCodeByElement(replacedText);

    replacedText =
      videoShortCodeService.replaceShortCodeByElement(replacedText);

    setContent(replacedText);
  }, [page, page?.body]);

  useEffect(() => {
    textIntroShortCodeService.renderComponent(({ text }) => {
      return (
        <ThemeProvider theme={theme}>
          <TextIntro text={text} />
        </ThemeProvider>
      );
    });
  }, [content, theme]);

  useEffect(() => {
    plusMinusShortCodeService.renderComponent(({ plus, minus }) => {
      const [pluses, minuses] = [plus.split(';'), minus.split(';')];

      return (
        <ThemeProvider theme={theme}>
          <ProsAndCons pros={pluses} cons={minuses} />
        </ThemeProvider>
      );
    });
  }, [content, theme]);

  useEffect(() => {
    galleryShortCodeService.renderComponent(({ number }) => {
      const slides = page?.galleries?.[Number(number) - 1];

      if (!slides) {
        return null;
      }

      return (
        <ThemeProvider theme={theme}>
          <ModalProvider>
            <ImagesSlider slides={slides} />
          </ModalProvider>
        </ThemeProvider>
      );
    });
  }, [content, page?.galleries, theme]);

  useEffect(() => {
    blockquoteShortCodeService.renderComponent(({ text, author, position }) => {
      return (
        <ThemeProvider theme={theme}>
          <BlockQuote
            text={text}
            author={{
              name: author,
              position: position,
            }}
          />
        </ThemeProvider>
      );
    });
  }, [content, theme]);

  useEffect(() => {
    listWithTitleShortCodeService.renderComponent(({ title, features }) => {
      const featuresList = features.split(';');

      if (!featuresList) {
        return null;
      }
      return (
        <ThemeProvider theme={theme}>
          <SportMode title={title} items={featuresList} />
        </ThemeProvider>
      );
    });
  }, [content, theme]);

  useEffect(() => {
    videoShortCodeService.renderComponent(({ id, label }) => {
      if (!id) {
        return null;
      }
      return (
        <ThemeProvider theme={theme}>
          <Youtube src={id} caption={label} />
        </ThemeProvider>
      );
    });
  }, [content, theme]);

  if (!page) {
    return null;
  }

  return (
    <Component
      id="post-content"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

export default Content;

const Component = styled.section`
  color: ${(props) => props.theme.black};

  > h1,
  > h2,
  > h3,
  > h4,
  > h5,
  > h6,
  > p,
  > ul,
  ol,
  > .text > p {
    margin: 0 auto;
    padding: 0 20px;
    width: 100%;
    max-width: 67%;
    margin-left: auto !important;
    color: ${(props) => props.theme.black};

    ${media.tablet(css`
      max-width: 100%;
      margin-left: 0 !important;
    `)}
  }

  > h3 {
    margin: 25px auto 50px;
    padding: 0 20px;
    width: 100%;
    max-width: 67%;
    font-weight: 700;
    font-size: 28px;
    line-height: 120%;

    @media (min-width: 1400px) and (max-width: 1600px) {
      margin-bottom: 40px;
    }

    @media (max-width: 1400px) {
      font-size: 20px;
      margin-bottom: 30px;
    }

    ${media.mobile(css`
      margin-bottom: 20px;
    `)}
  }

  > p {
    margin-top: 20px;
    margin-bottom: 20px;

    font-weight: 300;
    font-size: 16px;
    line-height: 140%;
    color: ${(props) => props.theme.black100};

    @media (min-width: 1400px) {
      margin-top: 30px;
      margin-bottom: 30px;
      font-size: 19px;
    }

    @media (min-width: 1600px) {
      margin-top: 40px;
      margin-bottom: 40px;

      font-size: 22px;
    }
  }

  > i {
    display: block;

    font-weight: 300;
    font-size: 16px;
    line-height: 140%;
    color: ${(props) => props.theme.black100};

    margin-top: 20px;
    margin-bottom: 20px;

    @media (min-width: 1400px) {
      margin-top: 30px;
      margin-bottom: 30px;
      font-size: 24px;
    }

    @media (min-width: 1600px) {
      margin-top: 40px;
      margin-bottom: 40px;
      font-size: 28px;
    }
  }

  ul li {
    list-style-type: disc;
    margin-top: 10px;
    font-size: 19px;
    position: relative;
    padding-left: 15px;
    margin-left: 5px;

    &:first-child {
      margin-top: 0;
    }
  }

  figure {
    margin: 20px auto;
    padding: 0 20px;
    width: 100%;
    max-width: 67%;

    ${media.tablet(css`
      max-width: 100%;
    `)}

    img {
      width: 100%;
      margin: 0 auto;
      max-height: 821px;
    }

    figcaption {
      margin: 15px auto 0;
      font-size: 18px;
      max-width: 920px;
      font-weight: 300;
      line-height: 140%;
      text-align: center;
      color: ${(props) => props.theme.black};
      opacity: 0.5;

      ${media.tablet(css`
        font-size: 16px;
      `)}
    }
  }

  img {
    width: 100%;
    margin: 0 auto;
    max-height: 821px;
  }

  a {
    color: ${colors.main};
  }
`;
